// src/components/Dashboard/shared.tsx
// ============================================
// CEO Dashboard 共享 UI 组件（Sprint 8 Epic 4）
//
// 设计风格：
//   - 暗色科技风：背景 #0a0e1a / 卡片 #131826 / 边框 #1e2a3f
//   - 玻璃拟态：backdrop-blur + 半透明
//   - 高信息密度（Bloomberg Terminal 风格）
//   - 等宽字体显示数字
//   - 数据降级时显示「--」并标注"数据待接入"
// ============================================

import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Lock } from "lucide-react";

// ============================================
// DashboardCard - 玻璃拟态卡片
// ============================================

interface DashboardCardProps {
  title: string;
  subtitle?: string;
  /** 强调色（顶部光带） */
  accentColor?: string;
  /** 右上角额外内容 */
  extra?: ReactNode;
  children: ReactNode;
  className?: string;
  /** 内容区域高度（不含 header） */
  bodyClassName?: string;
}

export function DashboardCard({
  title,
  subtitle,
  accentColor = "#3B82F6",
  extra,
  children,
  className = "",
  bodyClassName = "",
}: DashboardCardProps) {
  return (
    <div
      className={`relative rounded-xl border border-[#1e2a3f] bg-[#131826]/80 backdrop-blur-md overflow-hidden ${className}`}
    >
      {/* 顶部光带 */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${accentColor}80, transparent)`,
        }}
      />
      {/* 标题栏 */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1e2a3f]/60">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-1 h-3.5 rounded-full flex-shrink-0"
            style={{ background: accentColor }}
          />
          <h3 className="text-[12px] font-medium text-white tracking-tight truncate">
            {title}
          </h3>
          {subtitle && (
            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider truncate">
              {subtitle}
            </span>
          )}
        </div>
        {extra && <div className="flex-shrink-0">{extra}</div>}
      </div>
      {/* 内容 */}
      <div className={`p-3 ${bodyClassName}`}>{children}</div>
    </div>
  );
}

// ============================================
// KpiCell - 单个 KPI 单元
// ============================================

interface KpiCellProps {
  label: string;
  value: number | string | null | undefined;
  /** 格式化函数 */
  format?: (v: number) => string;
  /** 单位（如 ¥ / %） */
  unit?: string;
  /** 同比变化（正=上升） */
  delta?: number;
  /** 强调色 */
  color?: string;
  /** 副标题 */
  subtitle?: string;
  degraded?: boolean;
  /** v6.3.23 提示（如今日内容按品牌分类明细） */
  hint?: string;
}

export function KpiCell({
  label,
  value,
  format,
  unit,
  delta,
  color = "#10B981",
  subtitle,
  degraded = false,
  hint,
}: KpiCellProps) {
  const isNumber = typeof value === "number";
  const display =
    degraded || value === null || value === undefined
      ? "--"
      : isNumber && format
      ? format(value as number)
      : isNumber
      ? (value as number).toLocaleString()
      : String(value);

  const deltaColor =
    delta === undefined
      ? "text-gray-500"
      : delta > 0
      ? "text-emerald-400"
      : delta < 0
      ? "text-red-400"
      : "text-gray-500";
  const deltaStr =
    delta === undefined
      ? ""
      : `${delta > 0 ? "↑" : delta < 0 ? "↓" : "→"} ${Math.abs(delta).toFixed(1)}%`;

  return (
    <div className="relative rounded-lg border border-[#1e2a3f]/70 bg-[#0a0e1a]/60 px-3 py-2 overflow-hidden group hover:border-[#1e2a3f] transition-colors">
      <div
        className="absolute top-0 left-0 right-0 h-px opacity-50"
        style={{ background: `linear-gradient(90deg, ${color}, transparent)` }}
      />
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
          {label}
        </span>
        {degraded && (
          <Lock className="w-2.5 h-2.5 text-amber-500/70" />
        )}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span
          className="text-[20px] font-mono font-bold tracking-tight"
          style={{ color: degraded ? "#6B7280" : color }}
        >
          {display}
        </span>
        {unit && !degraded && (
          <span className="text-[10px] font-mono text-gray-500">{unit}</span>
        )}
      </div>
      <div className="mt-0.5 flex items-center justify-between">
        <span className="text-[9px] font-mono text-gray-600">
          {subtitle || (degraded ? "数据待接入" : "")}
        </span>
        {deltaStr && (
          <span className={`text-[9px] font-mono ${deltaColor}`}>
            {deltaStr}
          </span>
        )}
      </div>
      {hint && !degraded && (
        <div className="mt-0.5 text-[8.5px] font-mono text-gray-600 truncate" title={hint}>
          {hint}
        </div>
      )}
    </div>
  );
}

// ============================================
// MetricRow - 单行指标（Bloomberg 风格）
// ============================================

interface MetricRowProps {
  label: string;
  value: number | string | null | undefined;
  format?: (v: number) => string;
  unit?: string;
  /** 与上一条对比（百分比） */
  delta?: number;
  degraded?: boolean;
  color?: string;
}

export function MetricRow({
  label,
  value,
  format,
  unit,
  delta,
  degraded = false,
  color,
}: MetricRowProps) {
  const display =
    degraded || value === null || value === undefined
      ? "--"
      : typeof value === "number" && format
      ? format(value)
      : typeof value === "number"
      ? value.toLocaleString()
      : String(value);

  const valueColor = color || (degraded ? "#6B7280" : "#E5E7EB");

  return (
    <div className="flex items-center justify-between py-1 px-2 border-b border-[#1e2a3f]/40 last:border-b-0 hover:bg-white/[0.02]">
      <span className="text-[11px] font-mono text-gray-400 truncate">
        {label}
      </span>
      <div className="flex items-baseline gap-1.5 flex-shrink-0">
        <span
          className="text-[12px] font-mono font-semibold"
          style={{ color: valueColor }}
        >
          {display}
        </span>
        {unit && !degraded && (
          <span className="text-[9px] font-mono text-gray-600">{unit}</span>
        )}
        {delta !== undefined && !degraded && (
          <span
            className={`text-[9px] font-mono ${
              delta > 0
                ? "text-emerald-400"
                : delta < 0
                ? "text-red-400"
                : "text-gray-500"
            }`}
          >
            {delta > 0 ? "↑" : delta < 0 ? "↓" : "→"}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================
// EmptyState - 空状态 / 降级提示
// ============================================

interface EmptyStateProps {
  message?: string;
  degraded?: boolean;
  /** v6.3.18 未接入原因（显示具体原因而非笼统"数据待接入"） */
  reason?: string;
}

export function EmptyState({
  message = "暂无数据",
  degraded = false,
  reason,
}: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-6 text-center px-3">
      {degraded ? (
        <AlertTriangle className="w-4 h-4 text-amber-500/60 mb-1.5" />
      ) : null}
      <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
        {degraded ? (reason || "数据待接入") : message}
      </p>
    </div>
  );
}

// ============================================
// Tag - 小标签
// ============================================

interface TagProps {
  children: ReactNode;
  color?: "emerald" | "amber" | "blue" | "purple" | "pink" | "red" | "gray";
}

const TAG_COLORS: Record<string, string> = {
  emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  amber: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  blue: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  purple: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  pink: "bg-pink-500/10 text-pink-400 border-pink-500/30",
  red: "bg-red-500/10 text-red-400 border-red-500/30",
  gray: "bg-gray-500/10 text-gray-400 border-gray-500/30",
};

export function Tag({ children, color = "gray" }: TagProps) {
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border ${TAG_COLORS[color]}`}
    >
      {children}
    </span>
  );
}

// ============================================
// FadeIn - 入场动画包装
// ============================================

interface FadeInProps {
  children: ReactNode;
  delay?: number;
  className?: string;
}

export function FadeIn({ children, delay = 0, className }: FadeInProps) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

// ============================================
// 工具函数
// ============================================

/** 格式化金额（元 → 万元） */
export function formatYuan(v: number): string {
  if (Math.abs(v) >= 10000) {
    return `${(v / 10000).toFixed(2)}万`;
  }
  return v.toLocaleString();
}

/** 格式化金额带 ¥ 前缀 */
export function formatYuanWithSymbol(v: number): string {
  if (Math.abs(v) >= 10000) {
    return `¥${(v / 10000).toFixed(2)}万`;
  }
  return `¥${v.toLocaleString()}`;
}

/** 格式化百分比 */
export function formatPercent(v: number): string {
  return `${v.toFixed(1)}%`;
}

/** 格式化播放/阅读量（k/w） */
export function formatViews(v: number): string {
  if (v >= 10000) return `${(v / 10000).toFixed(1)}w`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(v);
}

/** 格式化耗时（毫秒 → s） */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}
